import React, { Fragment, JSX, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  DeleteIcon,
  WorksheetIcon,
  LinkIcon,
  AddStandardIcon,
} from "../../../assets/icons/index";
import { IWorksheet } from "../../../model/interface/worksheet";
import {
  getStandardWorksheets,
  getWorksheetPreviewURL,
} from "../../../api/worksheets.service";
import Constant from "../../../utils/constant";
import DeleteModal from "../../shared/common/deleteModal";
import Loader from "../../shared/loader/loader";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Tooltip from "../../shared/common/ellipsisText";
import { XIcon } from "@heroicons/react/outline";
import {
  associateStandardWorksheets,
  deleteStandardWorksheet,
  validateStandardWorksheetAssociation,
} from "../../../api/standard.service";
import { toast } from "react-toastify";
import { useConfirmation } from "../../shared/confirmation/confirmationService";

interface WorksheetProps {
  standardId: number;
  standardData: any;
}

function Worksheet(props: WorksheetProps): JSX.Element {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const { standardId, standardData } = props;
  const navigate = useNavigate();
  const [worksheets, setWorksheets] = useState<IWorksheet[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [worksheetDelete, setWorksheetDelete] = useState<number>(0);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [worksheetURL, setWorksheetURL] = useState<string>("");
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showAssociateWorksheetPopup, setShowAssociateWorksheetPopup] =
    useState<boolean>(false);
  const [worksheetIds, setWorksheetIds] = useState<string>("");
  const confirm = useConfirmation();
  useEffect(() => {
    if (isLoaded) {
      bindWorksheets();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      bindWorksheets();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const bindWorksheets = async () => {
    setLoading(true);

    const response = getStandardWorksheets(standardId, pageNumber, pageSize);
    const data: any = (await response).data;
    setLoading(false);
    setWorksheets(data.data);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
  };

  const deleteRecord = () => {
    deleteStandardWorksheet(props.standardId, worksheetDelete).then(() => {
      setShowDeletePopup(false);
      setWorksheetDelete(0);
      bindWorksheets();
    });
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setWorksheetDelete(0);
  };

  const confirmDelete = (idToDelete: number) => {
    if (idToDelete) {
      setShowDeletePopup(true);
      setWorksheetDelete(idToDelete);
    }
  };

  const handleAddWorksheetRedirect = () => {
    navigate(Constant.ADDWORKSHEET, { state: { autoFillData: standardData } });
  };

  const downloadWS = async (id, worksheet) => {
    if (id) {
      setLoading(true);
      getWorksheetPreviewURL(id, worksheet).then((res) => {
        setWorksheetURL(res.data);
        setLoading(false);
        const newWindow = window.open(
          res.data,
          "_blank",
          "noopener,noreferrer"
        );
        if (newWindow) newWindow.opener = null;
      });
    } else {
      toast.warning("File not found");
    }
  };

  const onWorksheetAssociate = async () => {
    validateStandardWorksheetAssociation(worksheetIds, standardId).then(
      async (d) => {
        if (d.data.isValid === true) {
          const worksheetIdsArray = worksheetIds
            .split(",")
            .map((id) => Number(id));
          await associateStandardWorksheets(standardId, worksheetIdsArray).then(
            (response) => {
              setShowAssociateWorksheetPopup(false);
              bindWorksheets();
              toast.info(response.data);
              setWorksheetIds("");
            }
          );
        } else {
          let message = "<ul class='list-disc'>";
          d.data.linkedWorksheets.forEach((msg: string) => {
            message = message + "<li class='mt-2'>" + msg + "</li>";
          });
          message = message + "</ul>";
          confirm({
            variant: "info",
            title: "Worksheet already associated",
            description: message,
          });
        }
      }
    );
  };

  return (
    <Fragment>
      {loading && <Loader />}
      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}
      {showAssociateWorksheetPopup && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="rounded-lg inline-block align-bottom bg-white px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 h-auto">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowAssociateWorksheetPopup(false)}
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex border-b">
                <h2 className="text-gray-700 font-semibold text-xl">
                  Enter Worksheet ID(s)
                </h2>
              </div>

              <div className="mt-3 grid grid-cols-2">
                <input
                  value={worksheetIds}
                  onChange={(e) => setWorksheetIds(e.target.value)}
                  type="text"
                  autoComplete="off"
                  className=" mt-1 w-full block border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                />
              </div>
              <div className="flex pt-4">
                <button
                  type="button"
                  className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  onClick={() => onWorksheetAssociate()}
                >
                  Associate
                </button>
                <button
                  type="button"
                  className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  onClick={() => setShowAssociateWorksheetPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="text-gray-900 block px-4 py-4 text-sm shadow border-b border-gray-200 rounded-b-lg">
        <div>
          <div className="relative flex">
            <div className="w-full">
              <div id="dataContainer">
                <div className="flex w-full mt-1 mb-4 rounded-lg">
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleAddWorksheetRedirect()}
                      type="button"
                      className="flex items-center text-sm  cursor-pointer"
                    >
                      <AddStandardIcon />
                      Add Worksheet
                    </button>
                    <button
                      onClick={() => setShowAssociateWorksheetPopup(true)}
                      type="button"
                      className="ml-6 flex items-center text-sm  cursor-pointer"
                    >
                      <AddStandardIcon />
                      Associate Worksheet
                    </button>
                  </div>
                </div>
                <div>
                  <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-primary-color">
                        <tr>
                          <th
                            scope="col"
                            className=" px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                          >
                            Id
                          </th>
                          <th
                            scope="col"
                            className=" px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                          >
                            Title
                          </th>
                          <th
                            scope="col"
                            className=" px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                          >
                            <span className="flex gap-2">Description</span>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                          >
                            Worksheet URL
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                          >
                            Key URL
                          </th>

                          <th
                            scope="col"
                            className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {worksheets?.map((worksheet, index) => (
                          <tr
                            key={worksheet.worksheetId}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-100"
                            }
                          >
                            <td className="px-3 py-2  capitalize  text-sm text-left font-normal text-gray-500 relative">
                              <a
                                className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                href={Constant.WORKSHEETEDIT.replace(
                                  ":id",
                                  worksheet.worksheetId.toString()
                                )}
                              >
                                {worksheet.worksheetId}
                              </a>
                              {worksheet.isActive ? (
                                <span className="mr-1 h-4/5 w-0.5  bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                              ) : (
                                <span className="mr-1 h-4/5 w-0.5  bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                              )}
                            </td>
                            <td className="px-3 py-2  capitalize  text-sm text-left font-normal text-gray-500">
                              <a
                                className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                href={Constant.WORKSHEETEDIT.replace(
                                  ":id",
                                  worksheet.worksheetId.toString()
                                )}
                              >
                                {worksheet.title}
                              </a>
                            </td>
                            <td className="px-3 py-2  capitalize  text-sm text-left font-normal text-gray-500">
                              <a
                                className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                href={Constant.WORKSHEETEDIT.replace(
                                  ":id",
                                  worksheet.worksheetId.toString()
                                )}
                              >
                                <Tooltip text={worksheet.description} />
                              </a>
                            </td>
                            <td className="px-3 py-2 capitalize  text-sm font-normal text-gray-500">
                              {worksheet.worksheetFilePath && (
                                <WorksheetIcon
                                  title={
                                    !worksheet.worksheetFilePath
                                      ? "File not available"
                                      : "Download Worksheet"
                                  }
                                  className={`w-5 mx-auto cursor-pointer ${
                                    !worksheet.worksheetFilePath
                                      ? "text-red-500"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    downloadWS(
                                      worksheet.worksheetId,
                                      worksheet.worksheetFilePath
                                    );
                                  }}
                                >
                                  <a
                                    href={worksheetURL}
                                    target={"_blank"}
                                    rel="noreferrer"
                                  >
                                    {worksheet.worksheetFilePath}
                                  </a>
                                </WorksheetIcon>
                              )}
                            </td>
                            <td className="px-3 py-2 capitalize  text-sm  text-left font-normal text-gray-500">
                              {worksheet.worksheetKeyFilePath && (
                                <LinkIcon
                                  title={
                                    !worksheet.worksheetKeyFilePath
                                      ? "File not available"
                                      : "Download Worksheet"
                                  }
                                  className={`w-5 mx-auto cursor-pointer ${
                                    !worksheet.worksheetKeyFilePath
                                      ? "text-red-500"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    downloadWS(
                                      worksheet.worksheetId,
                                      worksheet.worksheetKeyFilePath
                                    );
                                  }}
                                >
                                  {worksheet.worksheetKeyFilePath}
                                </LinkIcon>
                              )}
                            </td>

                            <td className="px-3 py-2  text-sm text-center font-normal text-gray-700">
                              <div className="flex gap-3 justify-center">
                                <DeleteIcon
                                  onClick={() =>
                                    confirmDelete(worksheet.worksheetId)
                                  }
                                  className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className={`flex justify-between items-center ${
                      worksheets.length === 0 ? "hidden" : ""
                    }`}
                  >
                    <Pagination
                      pages={totalPages}
                      setPageNumber={setPageNumber}
                      ref={paginationRef}
                    />
                    <span>
                      <span className="text-sm pr-4 ml-auto text-left-700">
                        Showing {pageNumber} to{" "}
                        {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                        {totalRecords} results
                      </span>

                      <SelectPageSize
                        setPageSize={setPageSize}
                        type={"second"}
                        ref={pageSizeRef}
                      />
                    </span>
                  </div>

                  {worksheets.length === 0 && (
                    <p className="p-2">No Records Found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default Worksheet;
